<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { MENTORSHIP_STATUS_COLORS, MENTORSHIP_STATUS_LABELS } from '../constants/statuses'
import { createCaseFilters, useMentorshipCasesStore } from '../stores/mentorshipCasesStore'
import type { MentorshipCapabilities, MentorshipCaseListItem, MentorshipCaseStatus } from '../types'
import { mentorshipApi } from '../services/mentorshipApi'
import DateRangeFilterCard from '@/components/createdUi/DateRangeFilterCard.vue'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import type { IFilterStore } from '@/types/IFilterStore'
import { ERouteNames } from '@/router/ERouteNames'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { getApiError } from '@/composables/getApiError'

const store = useMentorshipCasesStore()
const router = useRouter()
const route = useRoute()
const theme = useTheme()
const { smAndDown } = useDisplay()
const mobileFiltersOpen = ref(false)
const { itemsPerPage } = useTableItemsPerPage('mentorshipCases', 25)
const { filters, currentSortString } = storeToRefs(store)
if (['mentor', 'mentee', 'all'].includes(String(route.query.view))) store.workspace = route.query.view as typeof store.workspace
const capabilities = ref<MentorshipCapabilities | null>(null)
const capabilityError = ref('')
const choiceError = ref('')
const headers = [
  { title: '№', key: 'number', width: 75 },
  { title: 'Сотрудник', key: 'mentee_name', filterKey: 'mentee', sortable: false, width: 220 },
  { title: 'Наставник', key: 'mentor_name', filterKey: 'mentor', sortable: false, width: 210 },
  { title: 'Подразделение', key: 'department_name', filterKey: 'department', sortable: false, width: 210 },
  { title: 'Окончание', key: 'end_date', filterKey: 'end', width: 170 },
  { title: 'Прогресс', key: 'progress_percent', sortable: false, width: 140 },
  { title: 'Статус', key: 'status', filterKey: 'status', width: 165 },
]
const filterColumns = headers.filter(header => header.filterKey)
const sortMap = { number: 'number', end_date: 'end_date', status: 'status' }
const filterConfigs: IFilterConfig[] = [
  { key: 'mentee', labelPrefix: 'Сотрудник: ', type: 'tree' },
  { key: 'mentor', labelPrefix: 'Наставник: ', type: 'tree' },
  { key: 'department', labelPrefix: 'Подразделение: ', type: 'tree' },
  { key: 'status', labelPrefix: 'Статус: ', type: 'status', getCustomLabel: value => MENTORSHIP_STATUS_LABELS[value as MentorshipCaseStatus] || value },
  { key: 'end', labelPrefix: 'Окончание: ', type: 'date_range', rangeStartKey: 'end_after', rangeEndKey: 'end_before', dateFormat: 'YYYY-MM-DD' },
]
const choiceRequests: Record<string, number> = {}
const filterStore = reactive<IFilterStore>({
  filterChoices: {},
  searchChoices: {},
  filterCursors: {},
  filterLoading: {},
  filterStrategies: {},
  async fetchFilterChoices(key, term = '', cursor) {
    const sequence = (choiceRequests[key] || 0) + 1
    choiceRequests[key] = sequence
    this.filterLoading[key] = true
    try {
      const params: Record<string, unknown> = { ...store.filterParams, workspace: store.workspace, field: key, search_field: term, cursor }
      delete params[key]
      const result = await mentorshipApi.getFilterChoices(params)
      if (choiceRequests[key] !== sequence) return
      const target = term ? this.searchChoices : this.filterChoices
      target[key] = cursor ? [...(target[key] || []), ...result.results] : result.results
      this.filterCursors[key] = result.next
      this.filterStrategies[key] = result.strategy || 'independent'
      choiceError.value = ''
    } catch (e) {
      if (choiceRequests[key] === sequence) choiceError.value = getApiError(e, 'Не удалось загрузить значения фильтра. Откройте фильтр повторно.')
    } finally {
      if (choiceRequests[key] === sequence) this.filterLoading[key] = false
    }
  },
})
async function load() {
  await store.fetchCases({ page_size: itemsPerPage.value })
}
const tableLogic = useTableFilters({
  filters,
  currentSortString,
  itemsPerPage,
  sortMap,
  filterConfigs,
  filterChoices: computed(() => filterStore.filterChoices) as any,
  searchChoices: computed(() => filterStore.searchChoices) as any,
  filterLoading: filterStore.filterLoading,
  fetchFilterChoices: key => filterStore.fetchFilterChoices(key),
  fetchData: async () => {
    store.page = 1
    await load()
  },
  nextCursor: ref(null),
  loadedItemsCount: computed(() => store.items.length),
  totalItemsCount: computed(() => store.total),
  resetFilters: () => {
    store.filters = createCaseFilters()
  },
  resetOnUnmount: false,
})
function prepareFilter(open: boolean, key: string) {
  if (!open) return
  if (key === 'end') {
    for (const field of ['end_after', 'end_before'] as const) tableLogic.tempValues.value[field] = dateLabel(filters.value[field])
  } else tableLogic.handleFilterClick(key)
}
function applyDates() {
  for (const key of ['end_after', 'end_before'] as const) {
    const value = tableLogic.tempValues.value[key] || ''
    const [day, month, year] = value.split('.')
    filters.value[key] = value ? `${year}-${month}-${day}` : null
  }
  tableLogic.filterMenus.value.end = false
  tableLogic.triggerServerFilter()
}
function updateOptions(options: { page: number; itemsPerPage: number; sortBy: typeof store.sortBy }) {
  const sort = options.sortBy[0]
  const nextSort = sort ? `${sort.order === 'desc' ? '-' : ''}${sort.key}` : '-number'
  const reset = currentSortString.value !== nextSort || itemsPerPage.value !== options.itemsPerPage
  store.page = reset ? 1 : options.page
  itemsPerPage.value = options.itemsPerPage
  currentSortString.value = nextSort
  load()
}
function dateLabel(value: string | null) {
  return value ? value.split('-').reverse().join('.') : ''
}
function rowClick(_: Event, row: { item: MentorshipCaseListItem }) {
  openCase(row.item.id)
}
const rowProps = ({ item }: { item: MentorshipCaseListItem }) => ({
  tabindex: 0,
  onKeydown: (event: KeyboardEvent) => {
    if (event.key === 'Enter') openCase(item.id)
  },
})
function openCase(id: string) {
  router.push({ name: ERouteNames.MENTORSHIP_CASE_DETAIL, params: { id }, query: route.query })
}
async function loadCapabilities() {
  try {
    capabilities.value = await mentorshipApi.getCapabilities()
    capabilityError.value = ''
  } catch (e) {
    capabilityError.value = getApiError(e, 'Не удалось загрузить права. Повторите загрузку.')
  }
}
watch(() => store.workspace, async value => {
  await router.replace({ query: { ...route.query, view: value === 'all' ? undefined : value } })
  store.page = 1
  load()
})
watch(() => route.name, (name, old) => {
  if (name === ERouteNames.MENTORSHIP_CASES && old !== name) load()
})
onMounted(loadCapabilities)
</script>

<template>
  <section class="mentorship-register">
    <header class="mb-4">
      <VTabs v-model="store.workspace" show-arrows>
        <VTab value="all">
          Все
        </VTab><VTab value="mentor">
          Я наставник
        </VTab><VTab value="mentee">
          Моё наставничество
        </VTab>
      </VTabs>
    </header>
    <header class="register-actions d-flex flex-wrap align-center gap-2 mb-4">
      <VBtn
        v-if="capabilities?.can_create"
        class="general-button"
        prepend-icon="ri-add-line"
        :to="{ name: ERouteNames.MENTORSHIP_CASE_CREATE, query: route.query }"
      >
        Назначить наставничество
      </VBtn>
      <VBtn
        v-if="capabilities?.can_manage"
        variant="outlined"
        color="secondary"
        prepend-icon="ri-book-open-line"
        :to="{ name: ERouteNames.MENTORSHIP_TEMPLATES, query: route.query }"
      >
        Программы и компетенции
      </VBtn>
      <VTooltip v-if="capabilities?.can_manage_settings" text="Настройки наставничества">
        <template #activator="{ props }">
          <VBtn
            v-bind="props"
            class="ml-auto"
            variant="text"
            icon="ri-settings-3-line"
            aria-label="Настройки наставничества"
            :to="{ name: ERouteNames.MENTORSHIP_SETTINGS, query: route.query }"
          />
        </template>
      </VTooltip>
    </header>
    <VAlert
      v-if="capabilityError"
      type="warning"
      variant="tonal"
      class="mb-3"
    >
      {{ capabilityError }} <VBtn variant="text" @click="loadCapabilities">
        Повторить
      </VBtn>
    </VAlert>
    <div class="wrapper registry-card">
      <VBtn
        v-if="smAndDown"
        variant="text"
        color="secondary"
        prepend-icon="ri-filter-3-line"
        class="justify-start ma-2"
        :append-icon="mobileFiltersOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
        :aria-expanded="mobileFiltersOpen"
        @click="mobileFiltersOpen = !mobileFiltersOpen"
      >
        Фильтры
      </VBtn>
      <form v-if="!smAndDown || mobileFiltersOpen" class="register-filters d-flex flex-wrap gap-2 align-center pa-4" @submit.prevent="tableLogic.applySearchFilter()">
        <VTextField
          v-model="tableLogic.searchFilter.value"
          class="registry-search"
          placeholder="Фильтр по ФИО, номеру или основанию"
          aria-label="Поиск в наставничестве"
          clearable
          hide-details
          autocomplete="off"
        />
        <VIconBtn
          v-if="tableLogic.searchFilter.value"
          type="submit"
          color="primary"
          variant="outlined"
          icon="ri-filter-2-line"
          class="rounded-sm"
          width="48"
          height="48"
          aria-label="Применить поиск"
        />
        <VSpacer />
        <VBtn
          v-if="tableLogic.hasActiveFilters.value"
          variant="outlined"
          color="secondary"
          height="48"
          prepend-icon="ri-delete-bin-line"
          @click="tableLogic.renewDataAndResetFilters()"
        >
          Сбросить фильтры
        </VBtn>
        <VIconBtn
          icon="ri-refresh-line"
          variant="text"
          :loading="store.loading"
          aria-label="Обновить реестр"
          @click="load"
        />
      </form>
      <div v-if="tableLogic.activeFilterChips.value.length" class="d-flex flex-wrap gap-2 px-4 pb-3">
        <VChip
          v-for="chip in tableLogic.activeFilterChips.value"
          :key="`${chip.config?.key}-${chip.value}`"
          closable
          @click:close="tableLogic.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>
      <VAlert
        v-if="store.error || choiceError"
        type="error"
        variant="tonal"
        class="mx-4 mb-3"
      >
        {{ store.error || choiceError }} <VBtn v-if="store.error" variant="text" @click="load">
          Повторить
        </VBtn>
      </VAlert>
      <div v-if="smAndDown && mobileFiltersOpen" class="d-flex flex-wrap gap-2 px-4 pb-3">
        <VBtn
          v-for="col in filterColumns"
          :key="col.key"
          variant="outlined"
          size="small"
          :prepend-icon="tableLogic.getFilterIcon(col.filterKey!)"
          @click="prepareFilter(true, col.filterKey!); tableLogic.filterMenus.value[col.filterKey!] = true"
        >
          {{ col.title }}
        </VBtn>
      </div>
      <VDataTableServer
        v-model:sort-by="store.sortBy"
        :page="store.page"
        :items-per-page="itemsPerPage"
        :headers="headers"
        :items="store.items"
        :items-length="store.total"
        :loading="store.loading"
        :items-per-page-options="[10, 25, 50, 100]"
        :row-props="rowProps"
        item-value="id"
        class="mentorship-table no-row-gaps cursor-pointer-table custom-data-table custom-data-table-footer"
        :class="{ 'dark-theme': theme.global.current.value.dark }"
        loading-text="Загрузка наставничества…"
        hover
        hide-no-data
        @update:options="updateOptions"
        @click:row="rowClick"
      >
        <template #top>
          <div v-if="!store.loading && !store.items.length" class="register-empty pa-6 text-center text-medium-emphasis" role="status">
            {{ store.error ? 'Реестр не загружен.' : tableLogic.hasActiveFilters.value ? 'По выбранным условиям записей нет. Измените или сбросьте фильтры.' : 'Наставничество пока не назначено.' }}
          </div>
        </template>
        <template v-for="col in filterColumns" :key="col.key" #[`header.${col.key}`]="{ column }">
          <div class="header-content d-flex align-center">
            <span>{{ column.title }}</span>
            <VIcon
              v-if="col.sortable !== false"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon(col.key)"
              :class="{ 'is-active': tableLogic.isSortActive(col.key), 'is-desc': tableLogic.isSortDesc(col.key) }"
            />
            <VMenu
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom start"
              @update:model-value="prepareFilter($event, col.filterKey!)"
            >
              <template #activator="{ props }">
                <VIconBtn
                  v-bind="props"
                  variant="plain"
                  size="small"
                  class="filter-icon-btn"
                  :icon="tableLogic.getFilterIcon(col.filterKey!)"
                  :aria-label="`Фильтр: ${column.title}`"
                  :loading="filterStore.filterLoading[col.filterKey!]"
                  @click.stop
                />
              </template>
              <div @click.stop @keydown.stop>
                <DateRangeFilterCard
                  v-if="col.filterKey === 'end'"
                  :date-after="tableLogic.tempValues.value.end_after || ''"
                  :date-before="tableLogic.tempValues.value.end_before || ''"
                  @update:date-after="tableLogic.tempValues.value.end_after = $event"
                  @update:date-before="tableLogic.tempValues.value.end_before = $event"
                  @apply="applyDates"
                />
                <UniversalFilterTree
                  v-else
                  :field-key="col.filterKey!"
                  :store="filterStore"
                  :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
                  :placeholder="`Поиск: ${column.title}`"
                  @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
                  @apply="tableLogic.applyFilter(col.filterKey!)"
                  @cancel="tableLogic.filterMenus.value[col.filterKey!] = false"
                />
              </div>
            </VMenu>
          </div>
        </template>
        <template #item.mentee_name="{ item }">
          <div class="person-cell py-2">
            <RouterLink :to="router.resolve({ name: ERouteNames.MENTORSHIP_CASE_DETAIL, params: { id: item.id }, query: route.query }).href" @click.stop>
              {{ item.mentee_name }}
            </RouterLink><div v-if="item.assignment_kind === 'statutory'" class="text-caption">
              По законодательству
            </div>
          </div>
        </template>
        <template #item.mentor_name="{ item }">
          <div class="person-cell">
            {{ item.mentor_name || 'Не назначен' }}
          </div>
        </template>
        <template #item.department_name="{ item }">
          <div class="person-cell">
            {{ item.department_name || '—' }}
          </div>
        </template>
        <template #item.end_date="{ item }">
          <div class="text-no-wrap">
            {{ dateLabel(item.end_date) }}
          </div><div class="text-caption text-medium-emphasis">
            с {{ dateLabel(item.start_date) }}
          </div>
        </template>
        <template #item.progress_percent="{ item }">
          <div class="d-flex align-center gap-2">
            <VProgressLinear :model-value="Number(item.progress_percent)" height="6" rounded /><span>{{ Math.round(Number(item.progress_percent)) }}%</span>
          </div>
        </template>
        <template #item.status="{ item }">
          <VChip :color="MENTORSHIP_STATUS_COLORS[item.status]" size="small">
            {{ MENTORSHIP_STATUS_LABELS[item.status] }}
          </VChip>
        </template>
      </VDataTableServer>
    </div>
    <RouterView />
  </section>
</template>

<style scoped>
.mentorship-register { min-inline-size: 0; }
.registry-card { background: rgb(var(--v-theme-surface)); border-radius: 4px; }
.registry-search { flex: 0 1 380px; min-inline-size: 200px; }
.person-cell { min-inline-size: 150px; max-inline-size: 250px; white-space: normal; overflow-wrap: anywhere; }
.register-empty { min-inline-size: 0; max-inline-size: 100%; white-space: normal; overflow-wrap: anywhere; }
.mentorship-table :deep(.v-data-table__th) { background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important; font-size: 15px; font-weight: 500; white-space: nowrap; padding-inline: 1rem; }
.mentorship-table :deep(.v-data-table__td) { font-size: 15px; padding: 12px 1rem; }
.mentorship-table :deep(th:has(.custom-sort-icon) .v-data-table-header__sort-icon) { display: none; }
.custom-sort-icon { opacity: .4; }
.custom-sort-icon.is-active { opacity: 1; }
.custom-sort-icon.is-desc { transform: rotate(180deg); }
.mentorship-table :deep(.v-chip__content) { white-space: normal; }
.mentorship-table :deep(.v-data-table-footer) { justify-content: center; gap: 16px; }
@media (max-width: 600px) {
  .register-actions > .v-btn:not(.v-btn--icon) { flex: 1 1 auto; }
  .registry-search { flex: 1 1 220px; }
  .mentorship-table :deep(.v-data-table-footer) { flex-wrap: wrap !important; gap: 4px; padding-inline: 4px; }
  .mentorship-table :deep(.v-data-table-footer__pagination) { margin-inline-start: 0; }
}
</style>
